/**
 * FLAGS — gating por feature de los KINDS de módulo, registry-driven.
 *
 * La feature de un kind es la key del manifest de su plugin (p.ej.
 * `academy.module.forum`, `media.block.video`) — sin mapas dispersos: añadir
 * un kind nuevo lo gatea automáticamente. Compone con el mecanismo existente
 * `course_settings.enabled_module_kinds` (kind disponible = habilitado en la
 * lista Y feature efectiva-habilitada):
 *  · menú "añadir módulo" del authoring: los kinds deshabilitados desaparecen;
 *  · senda de aprendizaje: el contenido PRE-EXISTENTE de un kind deshabilitado
 *    se muestra como parada «deshabilitado por tu academia» (datos preservados),
 *    nunca desaparición muda.
 */
import { type Accessor, createMemo } from "solid-js";
import { featureGraph, parseFeatureOverrides, resolveFeatureEffective } from "@space/shared";
import { moduleRegistry } from "@/features/academy/modules/registry";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { useOrgSettings } from "@/features/academy/hooks/use-academy-data";
import { useCourseSettings } from "@/features/academy/hooks/use-course-modules";
import { usePlatformFlags } from "./use-feature-enabled";

export interface KindFeatureState {
  enabled: boolean;
  /** Key de la feature que lo bloquea (para el copy del stop). */
  blockedBy?: string;
  /** Key de la feature del kind (del manifest del plugin). */
  feature?: string;
}

/** Estado de feature por kind de módulo, reactivo al contexto (org activa + curso). */
export function useModuleKindFeatures(
  courseId: Accessor<string | undefined>,
): Accessor<(kind: string) => KindFeatureState> {
  const { activeOrgId } = useActiveOrg();
  const platform = usePlatformFlags();
  const orgSettings = useOrgSettings(() => activeOrgId());
  const courseSettings = useCourseSettings(courseId);
  const graph = featureGraph();

  const state = createMemo(() => {
    const platformMap = platform();
    const org = parseFeatureOverrides(orgSettings.data?.features);
    const course = parseFeatureOverrides(courseSettings.data?.features);
    return (kind: string): KindFeatureState => {
      const feature = moduleRegistry.get(kind)?.manifest.key;
      if (!feature) {
        return { enabled: true };
      }
      const st = resolveFeatureEffective(feature, { platformMap, org, course }, graph);
      return st.enabled
        ? { enabled: true, feature }
        : { enabled: false, feature, blockedBy: st.blockedBy };
    };
  });
  return state;
}
